/*
 * ------------------------------------------------------------------------------------------------------------------------
 *   EG · UGC stories
 *   Card carousel that opens a fullscreen, Instagram-style stories player.
 *   Ported from the Annkers ugc-section; adapted for Focal (no slider-component,
 *   so the card row is a native scroll-snap track driven from here).
 * ------------------------------------------------------------------------------------------------------------------------
 */

class UgcStories extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.tick = this.tick.bind(this);
  }

  connectedCallback() {
    this.popup = this.querySelector('[id^="UgcPopup-"]');
    this.previews = this.querySelectorAll(".ugc__preview");

    // --- Card carousel (replaces Dawn's slider-component) ---
    this.track = this.querySelector(".ugc__list");
    this.scrollPrev = this.querySelector('.ugc__control[name="previous"]');
    this.scrollNext = this.querySelector('.ugc__control[name="next"]');
    if (this.track) {
      this.track.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onScroll, { passive: true });
      this.updateScrollButtons();
    }

    if (!this.popup) {
      this.addEventListener("click", this.onClick);
      this.initLazyPreviews();
      return;
    }

    this.slides = Array.from(this.popup.querySelectorAll(".ugc__slide"));
    this.bars = Array.from(this.popup.querySelectorAll(".ugc__bar"));
    this.prevButton = this.popup.querySelector(".ugc__nav--prev");
    this.nextButton = this.popup.querySelector(".ugc__nav--next");
    this.index = 0;
    this.muted = false;
    this.paused = false;
    this.imageDuration = (parseInt(this.dataset.imageDuration, 10) || 5) * 1000;
    this.rafId = null;
    this.elapsed = 0;
    this.lastFrame = 0;

    this.slides.forEach((slide) => {
      const video = slide.querySelector("video");
      if (!video) return;

      video.addEventListener("timeupdate", () => {
        if (!slide.classList.contains("active") || !video.duration) return;
        const bar = this.bars[this.index];
        // Set the width directly so the CSS transition smooths out timeupdate's coarse steps.
        if (bar) bar.querySelector("span").style.width = `${(video.currentTime / video.duration) * 100}%`;
      });

      video.addEventListener("ended", () => this.change(this.index + 1));

      video.addEventListener("play", () => {
        if (slide.classList.contains("active")) this.setPaused(false);
      });

      video.addEventListener("pause", () => {
        if (slide.classList.contains("active") && !video.ended) this.setPaused(true);
      });
    });

    this.addEventListener("click", this.onClick);
    document.addEventListener("keydown", this.onKeydown);

    this.initLazyPreviews();
  }

  // The previews carry the autoplay attribute but ship without sources, so nothing
  // downloads until the card nears the viewport. Attaching the source and calling
  // load() lets the browser's own autoplay take over — iOS ignores a scripted play(),
  // but honours autoplay + muted + playsinline from the markup.
  initLazyPreviews() {
    if (!this.previews.length) return;

    const load = (video) => {
      const sources = video.querySelectorAll("source[data-src]");
      if (!sources.length) return;

      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute("data-src");
      });

      video.preload = "auto";
      video.load();
    };

    if (!("IntersectionObserver" in window)) {
      this.previews.forEach(load);
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          load(entry.target);
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: "200px" }
    );

    this.previews.forEach((video) => observer.observe(video));
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onScroll);
    this.stopTimer();
  }

  // --- Card carousel scrolling ---
  onScroll() {
    this.updateScrollButtons();
  }

  updateScrollButtons() {
    if (!this.track) return;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;
    const overflows = maxScroll > 1;
    if (this.scrollPrev) this.scrollPrev.disabled = !overflows || atStart;
    if (this.scrollNext) this.scrollNext.disabled = !overflows || atEnd;
  }

  scrollCards(direction) {
    if (!this.track) return;
    const card = this.track.querySelector(".ugc__item");
    const step = card ? card.getBoundingClientRect().width + 16 : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: step * direction, behavior: "smooth" });
  }

  onClick(event) {
    const trigger = event.target.closest("[data-action]");
    if (!trigger || !this.contains(trigger)) return;

    switch (trigger.dataset.action) {
      case "scroll-prev":
        this.scrollCards(-1);
        break;
      case "scroll-next":
        this.scrollCards(1);
        break;
      case "open-ugc":
        this.open(parseInt(trigger.dataset.index, 10) || 0);
        break;
      case "close-ugc":
        if (trigger === this.popup || trigger.classList.contains("ugc__close")) this.close();
        break;
      case "prev-ugc":
        this.change(this.index - 1);
        break;
      case "next-ugc":
        this.change(this.index + 1);
        break;
      case "toggle-ugc-sound":
        this.toggleSound();
        break;
      case "toggle-ugc-play":
        // The arrows are hidden on small screens, so the slide edges act as prev/next.
        if (trigger.classList.contains("ugc__slide") && this.hasTapZones()) {
          const rect = trigger.getBoundingClientRect();
          const x = event.clientX - rect.left;

          if (x < rect.width * 0.3) return this.change(this.index - 1);
          if (x > rect.width * 0.7) return this.change(this.index + 1);
        }

        this.togglePlay();
        break;
    }
  }

  onKeydown(event) {
    if (!this.popup.classList.contains("open")) return;

    if (event.key === "Escape") this.close();
    else if (event.key === "ArrowRight") this.change(this.index + 1);
    else if (event.key === "ArrowLeft") this.change(this.index - 1);
  }

  setBar(bar, width) {
    if (!bar) return;
    bar.classList.add("no-transition");
    bar.querySelector("span").style.width = width;
    void bar.offsetWidth;
    bar.classList.remove("no-transition");
  }

  setPaused(state) {
    this.paused = state;
    this.popup.classList.toggle("is-paused", state);
  }

  isImage(slide) {
    return slide.dataset.type === "image";
  }

  // Matches the breakpoint where the popup nav arrows are hidden in CSS.
  hasTapZones() {
    return window.matchMedia("(max-width: 989px)").matches;
  }

  // --- Image stories run on a timer instead of a video's own duration ---
  startTimer(fromStart = true) {
    this.stopTimer();
    if (fromStart) this.elapsed = 0;
    this.lastFrame = performance.now();
    this.rafId = requestAnimationFrame(this.tick);
  }

  stopTimer() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  tick(now) {
    this.elapsed += now - this.lastFrame;
    this.lastFrame = now;

    const progress = Math.min(this.elapsed / this.imageDuration, 1);
    this.setBar(this.bars[this.index], `${progress * 100}%`);

    if (progress >= 1) {
      this.rafId = null;
      this.change(this.index + 1);
      return;
    }

    this.rafId = requestAnimationFrame(this.tick);
  }

  change(next) {
    if (next < 0) return;
    if (next >= this.slides.length) return this.close();

    this.stopTimer();

    const prevSlide = this.slides[this.index];
    if (prevSlide) {
      const prevVideo = prevSlide.querySelector("video");
      if (prevVideo) {
        prevVideo.pause();
        prevVideo.currentTime = 0;
      }
      prevSlide.classList.remove("active");
    }

    this.index = next;

    this.bars.forEach((bar, i) => {
      bar.classList.toggle("watched", i < this.index);
      this.setBar(bar, i < this.index ? "100%" : "0%");
    });

    if (this.prevButton) this.prevButton.classList.toggle("is-disabled", this.index === 0);
    if (this.nextButton) this.nextButton.classList.toggle("is-disabled", this.index === this.slides.length - 1);

    const slide = this.slides[this.index];
    slide.classList.add("active");

    // The sound control is meaningless on an image story.
    this.popup.classList.toggle("is-image", this.isImage(slide));
    this.setPaused(false);

    if (this.isImage(slide)) {
      this.startTimer();
      return;
    }

    const video = slide.querySelector("video");
    video.muted = this.muted;
    video.currentTime = 0;
    video.play().catch(() => {
      // Autoplay with sound can be blocked — fall back to muted playback.
      this.muted = true;
      this.popup.classList.add("muted");
      video.muted = true;
      video.play().catch(() => this.setPaused(true));
    });
  }

  open(index) {
    this.popup.classList.add("open");
    this.popup.classList.toggle("muted", this.muted);
    document.documentElement.style.overflow = "hidden";
    this.change(index);
  }

  close() {
    this.stopTimer();
    this.popup.classList.remove("open", "is-paused", "is-image");
    document.documentElement.style.overflow = "";

    this.slides.forEach((slide) => {
      const video = slide.querySelector("video");
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      slide.classList.remove("active");
    });

    this.bars.forEach((bar) => {
      bar.classList.remove("watched");
      this.setBar(bar, "0%");
    });
  }

  toggleSound() {
    this.muted = !this.muted;
    this.popup.classList.toggle("muted", this.muted);

    const slide = this.slides[this.index];
    const video = slide && slide.querySelector("video");
    if (video) video.muted = this.muted;
  }

  togglePlay() {
    const slide = this.slides[this.index];
    if (!slide) return;

    if (this.isImage(slide)) {
      if (this.paused) {
        this.setPaused(false);
        this.startTimer(false);
      } else {
        this.setPaused(true);
        this.stopTimer();
      }
      return;
    }

    const video = slide.querySelector("video");
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }
}

if (!customElements.get("ugc-stories")) {
  customElements.define("ugc-stories", UgcStories);
}
